import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import {
  RestaurantCapabilityDto,
  CreateRestaurantCapabilityRequest,
  RestaurantCapabilityPermissionsRequest,
} from "../types/restaurantCapability";

type ApiResponseV2<T> = {
  success: boolean;
  message: string | null;
  data: T;
};

type ApiResponse = {
  success: boolean;
  message: string | null;
};

const capabilityKeys = {
  all: ["restaurant-capabilities"] as const,
};

// GET /web/restaurant-capabilities — Admin only
export const useRestaurantCapabilities = () =>
  useQuery({
    queryKey: capabilityKeys.all,
    queryFn: async (): Promise<RestaurantCapabilityDto[]> => {
      const res = await fetch(`${API_URL}/restaurant-capabilities`, {
        credentials: "include",
      });
      const body: ApiResponseV2<{ capabilities: RestaurantCapabilityDto[] }> =
        await res.json();
      return body.data.capabilities;
    },
  });

// POST /web/restaurant-capabilities — Admin only
export const useCreateRestaurantCapability = (
  onSuccess?: (capability: RestaurantCapabilityDto) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CreateRestaurantCapabilityRequest,
    ): Promise<RestaurantCapabilityDto> => {
      const res = await fetch(`${API_URL}/restaurant-capabilities`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body: ApiResponseV2<RestaurantCapabilityDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to create capability");
      return body.data;
    },
    onSuccess: (capability) => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      onSuccess?.(capability);
    },
  });
};

// POST /web/restaurant-capabilities/{id}/permissions — Admin only
export const useAttachCapabilityPermissions = (
  onSuccess?: (capability: RestaurantCapabilityDto) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      capabilityId,
      permissionIds,
    }: RestaurantCapabilityPermissionsRequest & {
      capabilityId: string;
    }): Promise<RestaurantCapabilityDto> => {
      const res = await fetch(
        `${API_URL}/restaurant-capabilities/${capabilityId}/permissions`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ permissionIds }),
        },
      );
      const body: ApiResponseV2<RestaurantCapabilityDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to attach permissions");
      return body.data;
    },
    onSuccess: (capability) => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      onSuccess?.(capability);
    },
  });
};

// DELETE /web/restaurant-capabilities/{id}/permissions — Admin only
export const useDetachCapabilityPermissions = (
  onSuccess?: (capability: RestaurantCapabilityDto) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      capabilityId,
      permissionIds,
    }: RestaurantCapabilityPermissionsRequest & {
      capabilityId: string;
    }): Promise<RestaurantCapabilityDto> => {
      const res = await fetch(
        `${API_URL}/restaurant-capabilities/${capabilityId}/permissions`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ permissionIds }),
        },
      );
      const body: ApiResponseV2<RestaurantCapabilityDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to detach permissions");
      return body.data;
    },
    onSuccess: (capability) => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      onSuccess?.(capability);
    },
  });
};

// POST /web/restaurant-capabilities/{id}/restaurants — Admin only
export const useAttachRestaurantToCapability = (
  onSuccess?: (capability: RestaurantCapabilityDto) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      capabilityId,
      restaurantIds,
    }: {
      capabilityId: string;
      restaurantIds: string[];
    }): Promise<RestaurantCapabilityDto> => {
      const res = await fetch(
        `${API_URL}/restaurant-capabilities/${capabilityId}/restaurants`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantIds }),
        },
      );
      const body: ApiResponseV2<RestaurantCapabilityDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to attach restaurant");
      return body.data;
    },
    onSuccess: (capability) => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      onSuccess?.(capability);
    },
  });
};

// DELETE /web/restaurant-capabilities/{id}/restaurants — Admin only
export const useDetachRestaurantFromCapability = (
  onSuccess?: (capability: RestaurantCapabilityDto) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      capabilityId,
      restaurantIds,
    }: {
      capabilityId: string;
      restaurantIds: string[];
    }): Promise<RestaurantCapabilityDto> => {
      const res = await fetch(
        `${API_URL}/restaurant-capabilities/${capabilityId}/restaurants`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantIds }),
        },
      );
      const body: ApiResponseV2<RestaurantCapabilityDto> = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to detach restaurant");
      return body.data;
    },
    onSuccess: (capability) => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      onSuccess?.(capability);
    },
  });
};

// DELETE /web/restaurant-capabilities/{id} — Admin only
export const useDeleteRestaurantCapability = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_URL}/restaurant-capabilities/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const body: ApiResponse = await res.json();
      if (!body.success)
        throw new Error(body.message ?? "Failed to delete capability");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      onSuccess?.();
    },
  });
};
